/**
 * Bilingual setup — English and Urdu (gap D10).
 *
 * WHY THIS IS PHASE 0/1 AND NOT "LATER"
 * A Canal Patwari (BPS-11) and a Sub-Engineer on a canal bank are not going to
 * operate an English-only interface reliably, and the data they enter — names,
 * father's name, Mauza, Chak, the stance of the accused — is natively Urdu.
 * More importantly, RTL cannot be retrofitted: every physical margin, padding
 * and flex direction in a finished LTR layout has to be revisited. Wiring it
 * before the screens exist costs nothing; wiring it afterwards is a rewrite.
 *
 * Bilingualism here is not only about LABELS. Several DATA fields are bilingual
 * pairs (name/nameUr, mauza/mauzaUr) because the printed Parcha Tawan and
 * hearing notice must render the Urdu values.
 *
 * `ur` is typed against the shape of `en`, so a key added to one and forgotten
 * in the other is a compile error rather than an English string appearing
 * mid-sentence in an otherwise Urdu screen.
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { en } from './locales/en';
import { ur } from './locales/ur';

export const SUPPORTED_LANGUAGES = ['en', 'ur'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const RTL_LANGUAGES: readonly Language[] = ['ur'];

export function isRtl(lang: string): boolean {
  return RTL_LANGUAGES.includes(lang as Language);
}

void i18next.use(initReactI18next).init({
  resources: { en: { translation: en }, ur: { translation: ur } },
  lng: 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false }, // React already escapes
});

/** Keep <html lang> and <html dir> in step with the active language. */
export function applyLanguageToDocument(lang: Language): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = isRtl(lang) ? 'rtl' : 'ltr';
}

export default i18next;
